import { makeStyles } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import * as React from "react";

import Footer from "@/components/Footer";
import { PublicHeader } from "@/components/PublicHeader";
import StepIndicator from "@/components/StepIndicator";
import { useDevice } from "@/hooks/useDevice";
import theme from "@/theme";

const useStyles = makeStyles(() => ({
  title: {
    color: theme.palette.text.primary,
    textAlign: "center",
    lineHeight: 1.5,
  },
  message: {
    color: theme.palette.text.primary,
    marginTop: 4,
  },
  errorMessage: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    fontSize: theme.typography.caption.fontSize,
  },
  phaseTags: {
    marginBottom: theme.spacing(2),
  },
}));

type Background = "default" | "primary";

type Props = {
  children: React.ReactNode;
  /** 画面上部に表示するフェーズチップ（<DiagnosisInvestmentStepChips /> 等を渡す）。渡すと上paddingが詰まる */
  phaseTags?: React.ReactNode;
  /** StepIndicator を出す場合のみ totalSteps/currentStep をセットで渡す */
  totalSteps?: number;
  currentStep?: number;
  title?: string;
  canSelectMultiple?: boolean;
  subMessage?: string;
  background?: Background;
  /** 認証後に共用する画面ではTopBar等を渡す。SPのヘッダーは各フローで扱う。 */
  pcHeader?: React.ReactNode;
};

const BACKGROUND_COLOR: Record<Background, string> = {
  default: theme.palette.background.default,
  primary: theme.palette.primary.main,
};

/*
 * RegistrationTemplateは、登録〜診断フローで共通利用するラッパーテンプレートです。
 * 外枠（背景・上下padding・中央寄せの1カラム・PCのFooter）だけを担い、
 * StepIndicator / title / phaseTags は渡されたときのみ表示します。
 */
export const RegistrationTemplate: React.FC<Props> = ({
  children,
  phaseTags,
  totalSteps,
  currentStep,
  title,
  canSelectMultiple,
  subMessage,
  background = "default",
  pcHeader = <PublicHeader />,
}) => {
  const { isMobile } = useDevice();
  const classes = useStyles();
  const showStepIndicator = totalSteps !== undefined && currentStep !== undefined;

  return (
    <Box
      bgcolor={BACKGROUND_COLOR[background]}
      pt={isMobile ? (phaseTags ? 8 : 10) : 0}
      textAlign="left"
      minHeight="100vh"
      display="flex"
      flexDirection="column"
    >
      {!isMobile && pcHeader}
      <Box
        pt={isMobile ? 0 : 4}
        width={isMobile ? "100%" : "390px"}
        px={3}
        pb={15}
        margin="0 auto"
        flexGrow={1}
        display="flex"
        flexDirection="column"
      >
        {phaseTags && <Box className={classes.phaseTags}>{phaseTags}</Box>}
        {showStepIndicator && <StepIndicator steps={totalSteps} currentStep={currentStep} />}
        {title && (
          <Typography variant="h2" className={classes.title}>
            {title}
          </Typography>
        )}
        {canSelectMultiple && (
          <Typography variant="body1" className={classes.errorMessage}>
            （一点以上選択必須）
          </Typography>
        )}
        {subMessage && (
          <Typography variant="body1" className={classes.message}>
            {subMessage}
          </Typography>
        )}
        {children}
      </Box>
      {!isMobile && <Footer />}
    </Box>
  );
};
